// combatpro/CombatProEngineApp.js
//
// Combat PRO - Battle Engine (Stage 33: view-shell decomposition).
//
// Stage 33 moves the 18 activeView view gates OUT of this file into per-view
// components under combatpro/views/, gated by combatpro/views/ViewRoot.js. The
// engine no longer holds the inline view markup; the #vi-view-root container now
// renders a single <ViewRoot activeView={activeView} />. The DOM output is
// identical: the wrapping <div id="vi-view-root"> remains here, ViewRoot renders
// only its gate children, and each view component reproduces the same markup,
// IDs, classes, styles, text, and existing map render layers verbatim.
//
// This is structural decomposition only. No gameplay, styling, route, layout,
// text, attribute, or behaviour changes. No new logic, no new handlers, no new
// state arrays, no new map() blocks. No imports from battletracker.html.
//
// WHAT STILL LIVES IN THIS FILE:
//   - The header, encounter bar, header controls, and layout wrappers
//     (.container -> .vi-layout -> .vi-main -> #vi-view-root).
//   - The registry-driven quicknav buttons (COMBAT_VIEW_REGISTRY, two grids).
//   - combat log -> rendered as <CombatLogPanel/>, kept as a body-level sibling
//     after .container to match the master spec's placement. It is not one of
//     the views and is not gated by activeView, so it stays here unchanged.
//
// WHAT MOVED (now in combatpro/views/, gated by ViewRoot.js):
//   - All 18 activeView gates, in the same source order, with the same equality
//     checks. view-monsters and view-spells remain panel components
//     (<MonsterPanel/> / <SpellPanel/>) and are gated inside ViewRoot; no
//     MonstersView/SpellsView files were created. view-monster-studio and
//     view-encounter remain PRESERVED EMPTY CONTAINER SHELLS in their own view
//     files, deferred to later component-extraction stages.
//
// State: view components self-serve state via useContext(CombatContext). This
// file still reads state.activeView for view gating and uses switchView for the
// quicknav buttons. CombatContext.js is unchanged.
//
// UI text that contains an em dash in the master spec is rendered via the
// Unicode escape {'\u2014'} so the visible output matches exactly while keeping
// no literal em dash character in this file.
//
// Do not edit battletracker.html. Do not wire this into App.js until a later
// approved stage.

import React, { useContext, useState, useEffect, useRef } from 'react';
import MonsterPanel from './panels/MonsterPanel';
import SpellPanel from './panels/SpellPanel';
import CombatLogPanel from './panels/CombatLogPanel';
import IntroOverlay from './panels/IntroOverlay';
import ShortcutsPanel from './panels/ShortcutsPanel';
import ConditionsPanel from './conditions/ConditionsPanel';
import SpellStudioModal from './panels/spells/SpellStudioModal';
import { CombatProvider, CombatContext } from './contexts/CombatContext';
import { MonsterSelectionProvider } from './contexts/MonsterSelectionContext';
import { COMBAT_VIEW_REGISTRY } from './shared/viewRegistry';
import { deriveFullName } from './monsters/monsterEngine';
import { familyArrayKey, activeFamilyOf, familyLabel, FAMILIES } from './encounter/encounterStoreEngine';
import MonsterTreasureModal from './treasure/MonsterTreasureModal';
import MigrationModal from './runtime/MigrationModal';
import './styles/combatProTheme.css';
import ViewRoot from './views/ViewRoot';
import ReadableSelect from './shell/ReadableSelect';
import CombatDock from './shell/CombatDock';
import {
  LayeredModalHost, LayeredModalProvider
} from './integration/ui/LayeredWorkspace';
import LayeredWorkspaceReference from './integration/ui/LayeredWorkspaceReference';
import PlayerVisibilityChip from './integration/PlayerVisibilityWorkspace';
import PrivateMessagesChip from './integration/PrivateCampaignMessages';
import InformationChip from './integration/InformationWorkspace';
import GeneratorCatalogueChip, { generatorContextForView } from './integration/GeneratorCatalogueWorkspace';
import GeneratorContextChip from './integration/GeneratorContextWorkspace';

function CombatProEngineView() {
  // Read the single active view and the view switcher from context. No optional
  // chaining: CombatProvider always supplies a non-null value.
  // Stage 39 (Initiative Integration): also consume currentRound for the round
  // display and the four initiative header handlers for the combat controls.
  const combatCtx = useContext(CombatContext);
  const { state, switchView, activateEncounter, setActiveMonster, nextCombatant, prevCombatant, nextRound, prevRound, rollInitiative, refreshSpellLibrary, createSnapshot, undoLastAction } = combatCtx;
  const activeView = state.activeView;
  const currentRound = state.currentRound;

  // Shell context-bar data (restored parity). The Active Encounter and Active
  // Monster selectors are driven from real Combat PRO state, not hardcoded.
  const encStore = state.encounterStore;
  // Encounter families (Random & Wave expansion): the CURRENT ENCOUNTER + Current
  // Encounter readout reflect the ACTIVE family; the ENCOUNTER TYPE pill chooses
  // which family the Active Encounter selector BROWSES (spec 5). browseFamily is a
  // header-local browse filter and never itself activates an encounter.
  const browseHook = useState(function () { return activeFamilyOf(state.encounterStore); });
  const browseFamily = browseHook[0];
  const setBrowseFamily = browseHook[1];
  const activeFam = activeFamilyOf(encStore);
  const activeArr = (encStore && Array.isArray(encStore[familyArrayKey(activeFam)])) ? encStore[familyArrayKey(activeFam)] : [];
  const activeEncIndex = encStore ? encStore.activeIndex : 0;
  const activeEncName = (activeArr[activeEncIndex] && activeArr[activeEncIndex].meta && activeArr[activeEncIndex].meta.name) ? activeArr[activeEncIndex].meta.name : 'Encounter 1';
  const encounters = (encStore && Array.isArray(encStore[familyArrayKey(browseFamily)])) ? encStore[familyArrayKey(browseFamily)] : [];
  const monsters = state.monsters || [];
  const monsterCount = Math.min(state.activeMonsterCount || monsters.length, monsters.length);
  const activeMonsterIndex = state.currentCombatantIndex;
  const [treasureOpen, setTreasureOpen] = useState(false);
  // Active Monster label: real monster name once populated (deriveTabName), with an
  // unobtrusive slot marker appended only when the same name appears in another slot.
  function activeMonsterLabel(monster, i) {
    var base = deriveFullName(monster, i);
    var raw = (monster && monster.customName && monster.customName.trim()) ? monster.customName.trim() : (monster && monster.selectedMonster ? monster.selectedMonster : '');
    if (!raw) { return base; }
    var dup = 0;
    for (var j = 0; j < monsterCount; j++) {
      var mj = monsters[j];
      if (!mj) { continue; }
      var nj = (mj.customName && mj.customName.trim()) ? mj.customName.trim() : (mj.selectedMonster || '');
      if (nj && nj === raw) { dup++; }
    }
    return dup > 1 ? (base + ' (#' + (i + 1) + ')') : base;
  }
  // Fully readable current values for the shell selectors (ReadableSelect faces):
  // the exact text of the selected option, never clipped or ellipsised.
  const browseSelEnc = (browseFamily === activeFam) ? encounters[activeEncIndex] : null;
  const activeEncFace = (browseFamily === activeFam)
    ? ((activeEncIndex + 1) + ' \u2014 ' + ((browseSelEnc && browseSelEnc.meta && browseSelEnc.meta.name) ? browseSelEnc.meta.name : ('Encounter ' + (activeEncIndex + 1))))
    : 'Select encounter\u2026';
  const activeMonsterFace = (monsterCount > 0 && activeMonsterIndex >= 0 && activeMonsterIndex < monsterCount)
    ? activeMonsterLabel(monsters[activeMonsterIndex], activeMonsterIndex)
    : '\u2014';

  // Stage 43 (Conditions Panel Integration): local visibility state for the
  // Active Conditions slide-in panel. The legacy toggleConditions (9102) toggled
  // a DOM class; here the parent owns an open/closed boolean and passes it to
  // ConditionsPanel. No CombatContext change is required.
  const [conditionsOpen, setConditionsOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Keyboard shortcuts (parity restoration). A single document keydown listener,
  // bound once, reads the latest handlers through a ref so it never goes stale.
  // Established mappings: Space Next Combatant, R Roll Initiative (current
  // monster), L Combat Log, K Shortcuts, N Next Round, P Prev Round, U Undo,
  // S Snapshot, C Conditions. Guarded so it never fires while the user is typing
  // in an input/textarea/select or a contenteditable field, and never hijacks a
  // browser shortcut (Ctrl/Cmd/Alt held).
  const kbdRef = useRef({});
  kbdRef.current = {
    nextCombatant: nextCombatant, nextRound: nextRound, prevRound: prevRound,
    undoLastAction: undoLastAction, createSnapshot: createSnapshot, rollInitiative: rollInitiative,
    currentIndex: state.currentCombatantIndex,
    setLogOpen: setLogOpen, setConditionsOpen: setConditionsOpen, setShortcutsOpen: setShortcutsOpen
  };
  useEffect(function () {
    function onKeyDown(e) {
      const tag = (e.target && e.target.tagName) ? e.target.tagName : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') { return; }
      if (e.target && e.target.isContentEditable) { return; }
      if (e.metaKey || e.ctrlKey || e.altKey) { return; }
      const k = kbdRef.current;
      if (e.code === 'Space') { e.preventDefault(); k.nextCombatant(); }
      else if (e.key === 'r' || e.key === 'R') { k.rollInitiative(k.currentIndex); }
      else if (e.key === 'l' || e.key === 'L') { k.setLogOpen(function (open) { return !open; }); }
      else if (e.key === 'k' || e.key === 'K') { k.setShortcutsOpen(function (open) { return !open; }); }
      else if (e.key === 'n' || e.key === 'N') { k.nextRound(); }
      else if (e.key === 'p' || e.key === 'P') { k.prevRound(); }
      else if (e.key === 'u' || e.key === 'U') { k.undoLastAction(); }
      else if (e.key === 's' || e.key === 'S') { k.createSnapshot(true); }
      else if (e.key === 'c' || e.key === 'C') { k.setConditionsOpen(function (open) { return !open; }); }
    }
    document.addEventListener('keydown', onKeyDown);
    return function () { document.removeEventListener('keydown', onKeyDown); };
  }, []);

  // Stage 52 (Custom Spell Studio UI - Editor Shell): own the Spell Studio modal
  // visibility and the active editing spell with local state, following the same
  // pattern as conditionsOpen above (ConditionsPanel). CombatContext is NOT used
  // for this - the legacy global vi_currentEditingSpell becomes editingSpell here.
  // openSpellStudio is the create-mode entry (editingSpell null, matching the
  // legacy vi_openAddSpellModal which set vi_currentEditingSpell = null). It is
  // threaded down the existing prop chain to the Add Spell button via ViewRoot ->
  // SpellPanel -> SpellWorkspace -> SpellToolbar. Edit-from-slot (editingSpell set)
  // is deferred to a later stage; only the Add (create) trigger is wired here.
  const [spellStudioOpen, setSpellStudioOpen] = useState(false);
  const [editingSpell, setEditingSpell] = useState(null);

  function openSpellStudio() {
    setEditingSpell(null);
    setSpellStudioOpen(true);
  }

  // Stage 53: edit-mode entry (legacy vi_editSpellFromSlot -> vi_loadSpellIntoModal).
  // Opens the Spell Studio with an existing spell so the editor populates from it.
  // SpellPanel resolves the slot's spell against the runtime spell DB and calls
  // this; threaded down via ViewRoot -> SpellPanel.
  function openSpellStudioForEdit(spell) {
    setEditingSpell(spell || null);
    setSpellStudioOpen(true);
  }

  // Stage 15: render the quicknav buttons from the registry instead of
  // duplicated hardcoded JSX. filter() preserves array order, which is
  // authoritative; no sorting.
  const leftButtons = COMBAT_VIEW_REGISTRY.filter(x => x.grid === 'left' && x.id !== 'view-monster-studio');
  const studioButton = COMBAT_VIEW_REGISTRY.find(x => x.id === 'view-monster-studio');
  const rightButtons = COMBAT_VIEW_REGISTRY.filter(x => x.grid === 'right');

  return (
    <div className="cp-root">
      <IntroOverlay />
      <CombatDock onOpenTreasure={() => setTreasureOpen(true)} />
      <div className="container">
        <header>
          <div className="vi-header-top">
            <div className="vi-header-side vi-header-side-left">
              <button className="shortcuts-toggle" onClick={() => setShortcutsOpen(open => !open)}>Shortcuts</button>
            </div>
            <div className="vi-header-identity">{'\u2694\uFE0F'} Combat PRO</div>
            <div className="vi-header-side vi-header-side-right">
              <GeneratorContextChip />
              <GeneratorCatalogueChip context={generatorContextForView(activeView)} />
              <InformationChip />
              <PrivateMessagesChip />
              <PlayerVisibilityChip />
              <button className={'dashboard-toggle' + (activeView === 'view-encounter-dashboard' ? ' active' : '')} onClick={() => switchView('view-encounter-dashboard')}>Dashboard</button>
            </div>
          </div>
          {activeView === 'view-encounter-dashboard' ? (
            <div className="vi-header-branding">
              <p className="vi-header-subtitle">Manage players monsters actions conditions initiative and full combat state in one unified system</p>
              <p className="vi-hint">© 2026 Visinvoke Pty Ltd. All rights reserved.<span className="vi-hint-tm">Visinvoke{'\u2122'} {'\u00b7'} Trademark Pending</span></p>
            </div>
          ) : null}
          <div className="vi-nav-bar">
            <div className="vi-sidebar">
              <button className="combat-log-toggle vi-combatlog-divider" onClick={() => setLogOpen(open => !open)}>Combat Log</button>
            </div>
            <div className="vi-nav-center">
              <div className="vi-nav-grids">
                <div className="vi-nav-grid-left">
                  <div className="vi-quicknav-grid">
                    {leftButtons.map(view => (
                      <button key={view.navId} className={activeView === view.id ? 'vi-quicknav-btn active' : 'vi-quicknav-btn'} aria-current={activeView === view.id ? 'true' : undefined} onClick={() => switchView(view.id)} id={view.navId} data-vi-section={view.section} data-tooltip={view.tooltip}>{view.label}</button>
                    ))}
                  </div>
                </div>
                <div className="vi-nav-grid-right">
                  <div className="vi-quicknav-grid">
                    {rightButtons.map(view => (
                      <button key={view.navId} className={activeView === view.id ? 'vi-quicknav-btn active' : 'vi-quicknav-btn'} aria-current={activeView === view.id ? 'true' : undefined} onClick={() => switchView(view.id)} id={view.navId} data-vi-section={view.section} data-tooltip={view.tooltip}>{view.label}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="vi-nav-subsystem-row">
                {studioButton ? (
                  <button className={(activeView === studioButton.id ? 'vi-quicknav-btn active' : 'vi-quicknav-btn') + ' vi-subsystem-btn'} aria-current={activeView === studioButton.id ? 'true' : undefined} onClick={() => switchView(studioButton.id)} id={studioButton.navId} data-vi-section={studioButton.section} data-tooltip={studioButton.tooltip}>{studioButton.label}</button>
                ) : null}
                <button className={(activeView === 'view-encounter-brief' ? 'vi-quicknav-btn active' : 'vi-quicknav-btn') + ' vi-subsystem-btn vi-subsystem-btn-brief'} aria-current={activeView === 'view-encounter-brief' ? 'true' : undefined} onClick={() => switchView('view-encounter-brief')} id="qnav-encounter-brief" data-tooltip="Encounter Brief: this battle's goal, setup, events (waves and phase changes) and special rules, tied to the active encounter.">{'\uD83D\uDCDC Encounter Brief'}</button>
                <button className={(activeView === 'view-treasure' ? 'vi-quicknav-btn active' : 'vi-quicknav-btn') + ' vi-subsystem-btn'} aria-current={activeView === 'view-treasure' ? 'true' : undefined} onClick={() => switchView('view-treasure')} id="qnav-treasure" data-tooltip="Open the full Treasure system: search monsters, generate hoards, containers and rewards, and track loot.">{'\uD83D\uDCB0 Treasure'}</button>
              </div>
            </div>
            <div className="vi-sidebar-right">
              <button className="combat-log-toggle vi-combatlog-divider" onClick={() => setConditionsOpen(open => !open)}>🎭 Conditions</button>
            </div>
          </div>
          <div className="vi-enc-howto">
            <span className="vi-enc-howto-text">Select an <strong>Active Monster</strong> to choose which monster the Combat PRO pages display and edit. Use <strong>Go To Page</strong> or the navigation above to open any system. Selecting a monster and then choosing <strong>Treasure</strong> opens the individual Treasure and search modal for that monster. Change the <strong>Active Encounter</strong> or <strong>Active Monster</strong> at any time to change the current working context.</span>
          </div>
          <div className="vi-enc-bar" id="vi-enc-bar">
            <div className="vi-enc-bar-left">
              <span className="vi-enc-eyebrow">Current Encounter</span>
              <span id="vi-enc-name">{familyLabel(activeFam).toUpperCase() + ' ' + (activeEncIndex + 1) + ' \u2014 ' + activeEncName}</span>
              <LayeredWorkspaceReference encounterName={activeEncName} currentRound={currentRound} />
            </div>
            <div className="vi-enc-bar-centre">
              <div className="vi-monster-selector cp-enctype-selector">
                <label className="vi-monster-selector-label">Encounter Type</label>
                <div className="cp-enctype-pills" role="group" aria-label="Encounter Type">
                  {FAMILIES.map(function (fam) { return (
                    <button type="button" key={fam} className={'cp-enctype-pill' + (browseFamily === fam ? ' is-on' : '')} onClick={function () { setBrowseFamily(fam); }}>{familyLabel(fam)}</button>
                  ); })}
                </div>
              </div>
              <div className="vi-monster-selector vi-sel-wide vi-sel-enc">
                <label className="vi-monster-selector-label">Active Encounter</label>
                <ReadableSelect id="vi-active-encounter" faceClassName="vi-page-nav-dropdown" ariaLabel="Active Encounter" faceText={activeEncFace} value={browseFamily === activeFam ? activeEncIndex : ''} onChange={(e) => { if (e.target.value !== '') { activateEncounter(browseFamily, parseInt(e.target.value, 10)); } }}>
                  {browseFamily !== activeFam ? <option value="">Select encounter\u2026</option> : null}
                  {encounters.map(function (enc, i) { return (
                    <option key={i} value={i}>{(i + 1) + ' \u2014 ' + ((enc && enc.meta && enc.meta.name) ? enc.meta.name : ('Encounter ' + (i + 1)))}</option>
                  ); })}
                </ReadableSelect>
              </div>
              <div className="vi-monster-selector vi-sel-wide vi-sel-mon">
                <label className="vi-monster-selector-label">Active Monster</label>
                <ReadableSelect faceClassName="vi-monster-selector-dropdown" ariaLabel="Active Monster" faceText={activeMonsterFace} value={activeMonsterIndex} onChange={(e) => setActiveMonster(parseInt(e.target.value, 10))}>
                  {monsters.slice(0, monsterCount).map((monster, i) => (
                    <option key={i} value={i}>{activeMonsterLabel(monster, i)}</option>
                  ))}
                </ReadableSelect>
              </div>
              <div className="vi-monster-selector vi-monster-selector-treasure">
                <label className="vi-monster-selector-label">Treasure</label>
                <button type="button" className="vi-treasure-quick" data-tooltip="Search the selected Active Monster for treasure. Results stay attached to that monster." onClick={() => setTreasureOpen(true)}>&#128176; Treasure</button>
              </div>
              <div className="vi-monster-selector vi-sel-page">
                <label className="vi-monster-selector-label">Go To Page</label>
                <ReadableSelect faceClassName="vi-page-nav-dropdown" ariaLabel="Go To Page" faceText="Select" value="" onChange={(e) => { if (e.target.value) { switchView('view-' + e.target.value); } }}>
                  <option value="">Select</option>
                  <option value="legendary">Legendary</option>
                  <option value="lair">Lair</option>
                  <option value="spells">Spells</option>
                  <option value="dice">Dice Roller</option>
                  <option value="resistance">Resistance</option>
                  <option value="last-actions">Last Actions</option>
                  <option value="effects">Effects</option>
                  <option value="notes">Notes</option>
                  <option value="abilities">Abilities</option>
                  <option value="weapons">Weapons</option>
                  <option value="morale">Morale</option>
                  <option value="ability-scores">Ability Scores</option>
                  <option value="selection">Selection</option>
                </ReadableSelect>
              </div>
            </div>
            <div className="vi-enc-bar-right">
              <span className="vi-enc-eyebrow">Encounters</span>
              <div className="cp-enc-nav-pills" role="group" aria-label="Manage encounters">
                <button className="cp-enc-nav-pill" data-tooltip="Open the Standard Encounter Dashboard - your 12 prepared encounters." onClick={() => switchView('view-encounter-dashboard')}>Standard</button>
                <button className="cp-enc-nav-pill" data-tooltip="Open the Random Encounter generator and its 12 slots." onClick={() => switchView('view-random-encounters')}>Random</button>
                <button className="cp-enc-nav-pill" data-tooltip="Open the Wave Encounter generator and its 12 slots." onClick={() => switchView('view-wave-encounters')}>Wave</button>
              </div>
            </div>
          </div>
          <div id="vi-enc-status-strip" className="vi-enc-strip"></div>
          <div id="vi-next-step" className="vi-next-step">Next Step: {'\u2014'}</div>
          <div className="vi-header-controls vi-combat-controls" id="combatControls">
            <div className="vi-cc-group vi-cc-group-round">
              <span className="vi-cc-label">Round</span>
              <button className="vi-btn vi-btn-secondary vi-cc-btn" onClick={prevRound}>{'\u23ee\uFE0F'} Prev</button>
              <div className="vi-round-display" id="currentRound">{currentRound}</div>
              <button className="vi-btn vi-btn-secondary vi-cc-btn" onClick={nextRound}>Next {'\u23ed\uFE0F'}</button>
            </div>
            <div className="vi-cc-sep" aria-hidden="true"></div>
            <div className="vi-cc-group vi-cc-group-init">
              <span className="vi-cc-label">Initiative</span>
              <button className="vi-btn vi-btn-secondary vi-cc-btn" onClick={prevCombatant}>{'\u23ee\uFE0F'} Prev Combatant</button>
              <button className="vi-btn vi-btn-secondary vi-cc-btn" onClick={nextCombatant}>{'\u2694\uFE0F'} Next Combatant</button>
            </div>
            <div className="vi-cc-sep" aria-hidden="true"></div>
            <div className="vi-cc-group vi-cc-group-session">
              <span className="vi-cc-label">Session</span>
              <button id="undoBtn" className="vi-btn vi-btn-info vi-cc-btn" disabled={state.historyStack.length === 0} onClick={undoLastAction} style={state.historyStack.length === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : { opacity: 1, cursor: 'pointer' }}>{'\u21a9\uFE0F'} Undo</button>
              <button className="vi-btn vi-btn-info vi-cc-btn" onClick={() => createSnapshot(true)}>{'\uD83D\uDCF8'} Snapshot</button>
              <div className="vi-saves-display">
                <span style={{ color: '#9AA4B2', fontWeight: 600 }}>Saves:</span>
                <span id="snapshotCount" className="vi-saves-count">{state.historyStack.length}</span>
                <span style={{ color: '#E6EDF3', fontWeight: 700 }}>/20</span>
              </div>
            </div>
          </div>
          <div id="vi-shell-sentinel" className="vi-shell-sentinel" aria-hidden="true"></div>
        </header>
        <div className="vi-layout">
          <div className="vi-main">
            <div id="vi-view-root">
              <ViewRoot activeView={activeView} onAddSpell={openSpellStudio} onOpenSpellEditor={openSpellStudioForEdit} />
            </div>

            <div className="vi-footer"></div>
          </div>
        </div>
      </div>

      <ShortcutsPanel isOpen={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <CombatLogPanel isOpen={logOpen} onClose={() => setLogOpen(false)} />
      {state.migrationPrompt ? <MigrationModal ctx={combatCtx} /> : null}
      <ConditionsPanel isOpen={conditionsOpen} onClose={() => setConditionsOpen(false)} />
      <SpellStudioModal isOpen={spellStudioOpen} editingSpell={editingSpell} onClose={() => setSpellStudioOpen(false)} onSaved={refreshSpellLibrary} />
      <MonsterTreasureModal index={activeMonsterIndex} isOpen={treasureOpen} onClose={() => setTreasureOpen(false)} />
      <LayeredModalHost />
    </div>
  );
}

function CombatProEngineApp() {
  return (
    <CombatProvider>
      <MonsterSelectionProvider>
        <LayeredModalProvider>
          <CombatProEngineView />
        </LayeredModalProvider>
      </MonsterSelectionProvider>
    </CombatProvider>
  );
}

export default CombatProEngineApp;
